import type { KeyboardEvent, ReactNode } from "react";
import { Link } from "react-router";
import { useNative } from "../../../shared/phone/media";
import { BackLink } from "../../components/ui/BackLink";
import { Icon } from "../../components/ui/Icon";
import { api } from "../../lib/api";
import { Foot, Spin } from "./Native";
import { initials } from "./text";

export interface ProductRef {
  id: string;
  name: string;
  logoR2Key: string | null;
  /** Whether the product gets cold email: only then does New goal offer it. */
  coldEmail: boolean;
}

/** A product's mark: its logo when it has one, else its initials (the prototype's `prodIcon` for a new product). */
export function ProductMark({ product }: { product: ProductRef }) {
  if (product.logoR2Key) return <img className="nwr" src={api.products.logoUrl(product.id)} alt="" />;
  return <i className="nwr">{initials(product.name)}</i>;
}

interface Props {
  back: { to: string; label: string };
  crumb: string;
  product?: ProductRef | null;
  /** The wizard's steps and the current one (1-based); steps before it that `canGo` allows can be clicked. */
  steps?: readonly string[];
  step?: number;
  canGo?: (step: number) => boolean;
  onGo?: (step: number) => void;
  title: string;
  /** One line under the title; without it the goal sentence shows, as in the prototype. */
  sub?: ReactNode;
  goalRef?: string;
}

/** The prototype's flow head (`.ph.flowhead`): back, crumbs, the step bars, one title and one line. */
export function FlowHead({ back, crumb, product, steps, step = 0, canGo, onGo, title, sub, goalRef }: Props) {
  const go = (i: number) => onGo?.(i);
  const key = (i: number) => (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      go(i);
    }
  };
  return (
    <header className="ph flowhead">
      <div className="ctx">
        <BackLink to={back.to} label={back.label} />
        <span className="crumbs fh-crumb">
          <b>{crumb}</b>
          {product && (
            <>
              <span className="dot" />
              <Link className="prod fh-prod" to={`/products/${product.id}`} title={`See the ${product.name} profile`}>
                <ProductMark product={product} />
                <span className="pn">{product.name}</span>
              </Link>
            </>
          )}
        </span>
        {steps && step > 0 && (
          <span className="ctx-r fprog">
            <span className="fprog-l">{steps[step - 1]}</span>
            <ol className="stages">
              {steps.map((label, k) => {
                const i = k + 1;
                const state = i < step ? "done" : i === step ? "on" : "";
                const nav = i < step && !!canGo?.(i);
                const aria = `${label}${state === "done" ? ", done" : state === "on" ? ", current step" : ""}`;
                return nav ? (
                  <li key={label} className={`${state} nav`} title={label} aria-label={aria} role="button" tabIndex={0} onClick={() => go(i)} onKeyDown={key(i)} />
                ) : (
                  <li key={label} className={state} title={label} aria-label={aria} />
                );
              })}
            </ol>
          </span>
        )}
      </div>
      <div className="ph-t wz-h">
        <h1 className="ptitle">{title}</h1>
      </div>
      {sub ? (
        <p className="ph-s">{sub}</p>
      ) : goalRef ? (
        <p className="ph-s goalref">
          <Icon name="target" />
          <span>{goalRef}</span>
        </p>
      ) : null}
    </header>
  );
}

/**
 * The prototype's sticky step bar (`.wz-nav`): an optional hint, Back, and the next step. On a phone and an
 * iPad it is the step's action bar at the bottom, and Back lives in the bar at the top.
 */
export function WizardNav({ hint, onBack, next, onNext, ok = true, busy = false }: { hint?: string; onBack?: () => void; next: string; onNext: () => void; ok?: boolean; busy?: boolean }) {
  const native = useNative();
  const off = !ok || busy;
  if (native)
    return (
      <Foot why={hint || null}>
        <button type="button" className="pn-btn pri wide" disabled={off} onClick={onNext}>
          {busy ? <Spin /> : null}
          {next}
          {next.startsWith("Next") ? <Icon name="right" /> : null}
        </button>
      </Foot>
    );
  return (
    <div className="wz-nav">
      <span className="sp">{hint}</span>
      {onBack && (
        <button type="button" className="btn ghost" onClick={onBack}>
          <Icon name="left" />
          Back
        </button>
      )}
      <button type="button" className="btn" disabled={off} style={off ? { opacity: 0.45 } : undefined} onClick={onNext}>
        {next}
        <Icon name="right" />
      </button>
    </div>
  );
}
